import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["content"];

  connect() {
    if (this.hasContentTarget) {
      // Cache the popover element so we can still reach it after we move it
      // out of this.element (Stimulus target lookup is scoped to the
      // controller's element subtree).
      this._popover = this.contentTarget;
    }
  }

  show() {
    if (!this._popover) return;
    this.#detach();
    this._popover.showPopover();
    this.position();
  }

  hide() {
    if (!this._popover) return;
    this._popover.hidePopover();
    this.#reattach();
  }

  toggle() {
    if (!this._popover) return;
    if (this._popover.matches(":popover-open")) {
      this.hide();
    } else {
      this.show();
    }
  }

  position() {
    const triggerRect = this.element.getBoundingClientRect();
    const popover = this._popover;
    const popoverRect = popover.getBoundingClientRect();
    const padding = 8;

    let top = triggerRect.bottom + 8;
    let left = triggerRect.left;
    let positionAbove = false;

    // Keep popover from going off the right edge
    if (left + popoverRect.width > window.innerWidth - padding) {
      left = window.innerWidth - popoverRect.width - padding;
    }

    // Keep popover from going off the left edge
    if (left < padding) {
      left = padding;
    }

    // If popover would go below viewport, show it above the trigger
    if (triggerRect.bottom + popoverRect.height > window.innerHeight - padding) {
      top = triggerRect.top - popoverRect.height - 8;
      positionAbove = true;
    }

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;

    // Set arrow position class
    popover.classList.toggle("popover-above", positionAbove);
    popover.classList.toggle("popover-below", !positionAbove);

    // Position arrow horizontally to point at trigger center
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const arrowLeft = Math.max(12, Math.min(triggerCenter - left - 8, popoverRect.width - 28));
    popover.style.setProperty("--arrow-left", `${arrowLeft}px`);
  }

  // For hover behavior
  mouseEnter() {
    this.show();
  }

  mouseLeave() {
    this.hide();
  }

  disconnect() {
    this.#reattach();
  }

  // Move the popover element out of any transformed ancestor (e.g. a modal
  // with transform: translateX) so its position: fixed coordinates resolve
  // against the viewport rather than the modal's local frame.
  #detach() {
    if (!this._popover || this._popover.parentNode === document.body) return;
    this._originalParent = this._popover.parentNode;
    this._originalNextSibling = this._popover.nextSibling;
    document.body.appendChild(this._popover);
  }

  #reattach() {
    if (!this._popover || !this._originalParent) return;
    if (this._originalNextSibling && this._originalParent.contains(this._originalNextSibling)) {
      this._originalParent.insertBefore(this._popover, this._originalNextSibling);
    } else {
      this._originalParent.appendChild(this._popover);
    }
    this._originalParent = null;
    this._originalNextSibling = null;
  }
}
